import { FC } from "react";
import { Box, Alert, CircularProgress } from "@mui/material";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from "react-router-dom";
import { DepartmentProgress } from "./departmentProgress";
import { CommentsCard } from "../../../components/lifecycle/commentsCard";
import { InfoCard } from "./infoCard";
import {
  MarketAccess,
  Compliance,
  Legal,
  Risk,
  CreditRisk,
  Finance
} from "./mftTasks";
import { useSpecificBroker, useUpdateBroker } from "../dataHooks/brokerQueries";
import { CommentDto } from "../../../api/Api";
import { RequireBrokerLifecycleRole } from "../../../auth/RequireBrokerLifecycleRole";
import { isEntityVpEmail } from "../../../auth/permissions";
import { EntityVpProvider } from "../../../auth/entityVpContext";
import { EntityMergerProvider } from "../../../auth/entityMergerContext";
import { getCurrentCommentAuthor } from "../../../utils/commentUtils";
import { correctedEntityPath } from "../../../utils/entityPaths";

type Params = {
  id?: string;
};

export const Broker: FC = () => {
  const { id } = useParams<Params>();
  const { pathname } = useLocation();
  const { data: broker, isLoading, isFetching } = useSpecificBroker(id);

  const updateBroker = useUpdateBroker();

  // Anyone who can open the broker can comment, see the counterparty detail page.
  const handleAddComment = (comment: CommentDto) => {
    if (!broker) return;
    updateBroker.mutate({
      ...broker,
      comments: [...(broker?.comments || []), comment]
    });
  };

  const handleDeleteComment = (timestamp: string) => {
    if (!broker) return;
    updateBroker.mutate({
      ...broker,
      comments: (broker.comments || []).filter(
        (c) => c?.timestamp !== timestamp
      )
    });
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (broker === undefined) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Broker not found</Alert>
      </Box>
    );
  }

  // A merger opened under the Brokers tab (or the reverse) moves to the right tab, see the
  // counterparty detail page (including why the id must match first).
  const correctedPath = correctedEntityPath(
    "broker",
    pathname,
    broker.isMerger
  );
  if (correctedPath && broker.id === id) {
    return <Navigate to={correctedPath} replace />;
  }

  // A VP holds no app role, so lifecycle access for them is decided per broker from the vp
  // field rather than from the token.
  const isEntityVp = isEntityVpEmail(broker?.vp, getCurrentCommentAuthor());
  const isMerger = Boolean(broker.isMerger);

  return (
    <EntityVpProvider value={isEntityVp}>
      <EntityMergerProvider value={isMerger}>
        <Box flex={1} sx={{ overflowY: "auto" }}>
          <InfoCard specificBroker={broker} />
          <DepartmentProgress
            marketAccessProgress={broker?.progress?.marketAccess}
            complianceProgress={broker?.progress?.compliance}
            legalProgress={broker?.progress?.legal}
            riskProgress={broker?.progress?.risk}
            creditRiskProgress={broker?.progress?.creditRisk}
            financeProgress={broker?.progress?.finance}
          />
          <CommentsCard
            onAddComment={handleAddComment}
            onDeleteComment={handleDeleteComment}
            comments={(broker?.comments || []).filter(
              (comment): comment is CommentDto => Boolean(comment)
            )}
          />
          <Routes>
            {/* A merger has no Market Access lifecycle: its answers came in on the create form. */}
            {!isMerger && (
              <Route
                path="marketaccess"
                element={
                  <RequireBrokerLifecycleRole lifecycle="marketaccess">
                    <MarketAccess specificBroker={broker} key={broker?.id} />
                  </RequireBrokerLifecycleRole>
                }
              />
            )}
            <Route
              path="compliance"
              element={
                <RequireBrokerLifecycleRole lifecycle="compliance">
                  <Compliance specificBroker={broker} key={broker?.id} />
                </RequireBrokerLifecycleRole>
              }
            />
            <Route
              path="legal"
              element={
                <RequireBrokerLifecycleRole lifecycle="legal">
                  <Legal specificBroker={broker} key={broker?.id} />
                </RequireBrokerLifecycleRole>
              }
            />
            <Route
              path="risk"
              element={
                <RequireBrokerLifecycleRole lifecycle="risk">
                  <Risk specificBroker={broker} key={broker?.id} />
                </RequireBrokerLifecycleRole>
              }
            />
            <Route
              path="creditrisk"
              element={
                <RequireBrokerLifecycleRole lifecycle="creditrisk">
                  <CreditRisk specificBroker={broker} key={broker?.id} />
                </RequireBrokerLifecycleRole>
              }
            />
            <Route
              path="finance"
              element={
                <RequireBrokerLifecycleRole lifecycle="finance">
                  <Finance specificBroker={broker} key={broker?.id} />
                </RequireBrokerLifecycleRole>
              }
            />
          </Routes>
          {isFetching && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000
              }}
            >
              <CircularProgress size={72} />
            </Box>
          )}
        </Box>
      </EntityMergerProvider>
    </EntityVpProvider>
  );
};
